import type { RemovePresetsOptions, RemovePresetsResult } from '@cli-types/next-adapter/runtime.js'
import type { BetterstartPresetId } from '@cli-types/preset-engine/presets.js'
import { removeEnvVars } from '@cli-utils/files.js'
import { writeConfigFile } from '@core-engine/config/serialize/write-config-file.js'
import { flattenEnvKeys } from '@core-engine/installable/protocol/flatten-env-keys.js'
import { presetManifestStore } from '@preset-engine/manifests/preset-manifest-store.js'
import { removeDependenciesAsync } from '@next-adapter/init/scaffolders/dependencies/remove-dependencies-async.js'
import { getPresetDefinition } from '@next-adapter/preset-registry.js'
import { findBlockingSchemaDependencies } from './find-blocking-schema-dependencies.js'
import { normalizeInstalledPresets } from './normalize-installed-presets.js'
import { presetFiles } from './preset-files.js'
import { regenerateBarrels } from './regenerate-barrels.js'
import { removePresetSchemas } from './remove-preset-schemas.js'

export async function removePresets({
  cwd,
  config,
  pm,
  presetIds
}: RemovePresetsOptions): Promise<RemovePresetsResult> {
  const installedPresets = normalizeInstalledPresets(config)
  const removed: BetterstartPresetId[] = []
  const warnings: string[] = []

  for (const presetId of presetIds) {
    if (!installedPresets.includes(presetId)) {
      warnings.push(`${presetId} is not installed.`)
      continue
    }

    const manifest = presetManifestStore.load(cwd, presetId)
    if (!manifest) {
      throw new Error(`Missing manifest for installed preset "${presetId}".`)
    }

    const dependents = installedPresets.filter((installedPresetId) => {
      if (installedPresetId === presetId) {
        return false
      }

      return getPresetDefinition(installedPresetId).dependencies.includes(presetId)
    })

    if (dependents.length > 0) {
      throw new Error(
        `Cannot remove ${presetId}. The following presets depend on it: ${dependents.join(', ')}`
      )
    }

    const blockers = findBlockingSchemaDependencies(cwd, config, manifest.ownedSchemas)
    if (blockers.length > 0) {
      throw new Error(`Cannot remove ${presetId}:\n- ${blockers.join('\n- ')}`)
    }

    const deletedPaths = removePresetSchemas(cwd, config, manifest)

    for (const filePath of manifest.ownedFiles) {
      const relativeOutputPath = filePath.replace(`${config.paths.admin.replace(/^\.\//, '')}/`, '')
      const deletedPath = presetFiles.restoreOrDeleteFile(cwd, config, relativeOutputPath)
      if (deletedPath) {
        deletedPaths.push(deletedPath)
      }
    }

    presetManifestStore.remove(cwd, presetId)
    const nextInstalledPresets = installedPresets.filter(
      (installedPresetId) => installedPresetId !== presetId
    )
    installedPresets.splice(0, installedPresets.length, ...nextInstalledPresets)
    config.presets.installed = [...nextInstalledPresets].sort()

    const remainingPresets = normalizeInstalledPresets(config)
    const dependencyDiff = presetFiles.getUnusedDependencies(
      remainingPresets,
      presetId,
      config.integrations.installed
    )

    if (dependencyDiff.dependencies.length > 0 || dependencyDiff.devDependencies.length > 0) {
      const result = await removeDependenciesAsync({
        cwd,
        pm,
        dependencies: dependencyDiff.dependencies,
        devDependencies: dependencyDiff.devDependencies
      })

      if (!result.success) {
        warnings.push(result.error ?? `Failed to remove dependencies for ${presetId}.`)
      }
    }

    const usedEnvKeys = new Set(
      remainingPresets.flatMap((installedPresetId) =>
        flattenEnvKeys(getPresetDefinition(installedPresetId))
      )
    )
    const removableEnvKeys = manifest.envKeys.filter((envKey) => !usedEnvKeys.has(envKey))
    if (removableEnvKeys.length > 0) {
      removeEnvVars(cwd, removableEnvKeys)
    }

    if (manifest.ownedSchemas.length > 0) {
      await regenerateBarrels(cwd, config)
    }

    presetFiles.cleanupEmptyDirs(cwd, deletedPaths, config)

    writeConfigFile(cwd, config)
    removed.push(presetId)
  }

  return {
    removed,
    warnings,
    config
  }
}
